// Plan 2026-10-19 (Misch-EK ohne DR): Container-Status nur ueber Prozessknoepfe, „Bestand buchen“ und
// die Alt-Bestand-Bestaetigung im Misch-EK-Dialog. Reine Helfer; die Regeln spiegeln den Server
// (logic.ts pruefeProzessStatus/pruefeStatusKorrektur, ek-logic.ts mischEk), der Server prueft erneut.
import type {
  AltHerkunft, BestandAmpel, BestandBuchenSperre, BestandGrund, PurchaseShipment,
} from "@/api/purchase";

type ShipmentStatus = PurchaseShipment["status"];

const VOR_ANKUNFT: readonly ShipmentStatus[] = ["produktion", "abholbereit", "unterwegs"];

export interface ProzessKnopf { label: string; ziel: ShipmentStatus }

type StatusQuelle = Pick<PurchaseShipment, "status" | "warehouse_receipt_id" | "status_vor_parken">;

/** Knoepfe im Container: vorwaerts (Ueberspringen erlaubt), Parken, Fortsetzen nur in den Status vor dem Parken. */
export function prozessKnoepfe(s: StatusQuelle): ProzessKnopf[] {
  if (s.warehouse_receipt_id !== null) return [];
  if (s.status === "geparkt") {
    const vor = s.status_vor_parken ?? null;
    return vor !== null && VOR_ANKUNFT.includes(vor) ? [{ label: "Fortsetzen", ziel: vor }] : [];
  }
  const ab = VOR_ANKUNFT.indexOf(s.status);
  if (ab < 0) return [];
  const vorwaerts: ProzessKnopf[] = [];
  if (ab < 1) vorwaerts.push({ label: "Abholbereit", ziel: "abholbereit" });
  if (ab < 2) vorwaerts.push({ label: "Unterwegs", ziel: "unterwegs" });
  return [...vorwaerts, { label: "Parken", ziel: "geparkt" }];
}

/** Ziele von „Status korrigieren…“: nur rueckwaerts vor dem WE; aus geparkt jedes Ziel vor der Ankunft. */
export function korrekturZiele(s: StatusQuelle): ShipmentStatus[] {
  if (s.warehouse_receipt_id !== null) return [];
  if (s.status === "geparkt") return [...VOR_ANKUNFT];
  const ab = VOR_ANKUNFT.indexOf(s.status);
  return ab <= 0 ? [] : VOR_ANKUNFT.slice(0, ab);
}

export const KORREKTUR_GRUND_MIN = 10;

export const AMPEL_TEXT: Record<BestandAmpel, string> = {
  gruen: "eindeutig",
  gelb: "prüfen",
  rot: "unklar",
};

export const AMPEL_KLASSE: Record<BestandAmpel, string> = {
  gruen: "bg-success-soft text-success border-success/40",
  gelb: "bg-warn-soft text-warn border-warn/40",
  rot: "bg-coral-soft text-coral border-coral/40",
};

export const GRUND_TEXT: Record<BestandGrund, string> = {
  kette_bruch: "Bestandsverlauf lückenhaft",
  historie_fehlt: "Bestandsverlauf fehlt",
  mehrere_zugaenge: "mehrere große Zugänge",
  anderer_container: "Artikel liegt auch in einem anderen Container im Wareneingang",
  abweichung: "Zubuchung weicht von der eingelagerten Menge ab",
  uebrige_plus: "weitere Zugänge im Zeitraum",
  zugang_vor_fenster: "großer Zugang kurz vor dem Wareneingang",
  zuordnung_unklar: "dieselbe Zubuchung ist bei einem anderen Container unklar — Alt-Bestand prüfen",
  kleine_menge: "kleine Menge — Zubuchung nicht eindeutig",
};

export function grundText(g: string): string {
  return (GRUND_TEXT as Record<string, string>)[g] ?? g;
}

export const SPERRE_TEXT: Record<BestandBuchenSperre, string> = {
  order_archived: "Archivierte Bestellung zuerst wiederherstellen.",
  kein_we: "Der Container hat keinen Wareneingang.",
  wrong_status: "Bestand buchen geht nur aus „im Wareneingang“ — der Container ist schon eingelagert oder in einem anderen Status.",
  we_v1: "Wareneingang im alten Format — Bestand buchen nur für Kartonzählung.",
  we_nicht_abgeschlossen: "Erst den Wareneingang abschließen.",
  we_unvollstaendig: "Der Wareneingang hat offene oder teilweise Positionen.",
  we_klaerfaelle_offen: "Der Wareneingang hat offene Klärfälle.",
  import_laeuft: "Der Bestandsabruf läuft gerade — nach dem Abruf erneut öffnen.",
  nichts_eingelagert: "Keine Positionen eingelagert.",
};

const zahl = (n: number) => n.toLocaleString("de-DE");

function datum(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });
}

/** „Zubuchung am 01.10.2026: +10.000; davor 5.412“ bzw. „Keine Zubuchung gefunden — Bestand heute 37“. */
export function herkunftText(h: Pick<AltHerkunft, "dr_zubuchung" | "bestand_vor" | "quelle_bestand" | "treffer_am">): string {
  if (h.treffer_am !== null && h.dr_zubuchung !== 0) {
    return `Zubuchung am ${datum(h.treffer_am)}: +${zahl(h.dr_zubuchung)}; davor ${zahl(h.bestand_vor)}`;
  }
  return `Keine Zubuchung gefunden — Bestand heute ${zahl(h.bestand_vor)}`;
}

const round4 = (n: number) => Math.round(n * 10_000) / 10_000;

/** Anzeige-Nachrechnung bei geaenderter Alt-Menge; dieselbe Formel wie der Server (ek-logic mischEk). */
export function mischEkLokal(i: { altBestand: number; altEkEur: number | null; qty: number; landedEkEur: number | null }): number | null {
  if (i.landedEkEur === null) return null;
  if (i.altEkEur === null) return round4(i.landedEkEur);
  const alt = Math.max(0, i.altBestand);
  const neu = Math.max(0, i.qty);
  if (alt + neu <= 0) return round4(i.landedEkEur);
  return round4((alt * i.altEkEur + neu * i.landedEkEur) / (alt + neu));
}

interface AltPosition { stockItemId: number; altBestand: number; altPflicht?: boolean }

/** Gelbe/rote Koepfe muessen bestaetigt oder geaendert sein, bevor die Freigabe moeglich ist. */
export function altPflichtOffen(
  positionen: readonly AltPosition[], bestaetigt: Record<number, boolean>, geaendert: Record<number, number>,
): boolean {
  return positionen.some((p) => p.altPflicht === true && !bestaetigt[p.stockItemId]
    && (geaendert[p.stockItemId] === undefined || geaendert[p.stockItemId] === p.altBestand));
}

/** Body `alt_bestaetigt`: bestaetigte Pflicht-Koepfe und jeder geaenderte Kopf, je Kopf genau einmal. */
export function altBestaetigtBody(
  positionen: readonly AltPosition[], bestaetigt: Record<number, boolean>, geaendert: Record<number, number>,
): Array<{ stock_item_id: number; alt_bestand: number }> {
  const out: Array<{ stock_item_id: number; alt_bestand: number }> = [];
  for (const p of positionen) {
    const wert = geaendert[p.stockItemId] ?? p.altBestand;
    const istGeaendert = wert !== p.altBestand;
    if (istGeaendert || (p.altPflicht === true && bestaetigt[p.stockItemId])) {
      out.push({ stock_item_id: p.stockItemId, alt_bestand: wert });
    }
  }
  return out;
}
